/**
 * Journal entries, and the form that posts one.
 *
 * The screen is built around two facts about this ledger:
 *
 *   - A journal must balance. The form adds the debits and the credits as
 *     exact decimal strings and refuses to submit while they differ, because
 *     the server refuses too and a round trip is a worse way to learn it.
 *   - Posting is asynchronous. The submit records an event and enqueues it;
 *     acceptance is not proof of posting. The screen follows the event until
 *     it is posted, failed or skipped, and says which.
 *
 * A posted entry is never edited. It is reversed by an entry that says so.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Undo2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { formatCalendarDate } from "@/lib/format";
import { localizedName } from "@/lib/i18n";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  getBusinessEvent,
  listJournalEntries,
  reverseJournalEntry,
  submitManualJournal,
  type BusinessEvent,
  type ManualJournalLine,
} from "../journals-api";
import { useAccounts } from "../queries";

const TERMINAL_STATES = new Set(["posted", "failed", "skipped"]);

interface DraftLine {
  accountId: string;
  debit: string;
  credit: string;
}

const EMPTY_LINE: DraftLine = { accountId: "", debit: "", credit: "" };

/** Sums exact decimal strings without ever touching a float: money that is
 *  added as a number is money that is eventually wrong. */
function sumMinor(values: string[]): bigint {
  let total = 0n;
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === "") continue;
    const [whole = "0", fraction = ""] = trimmed.split(".");
    const padded = (fraction + "00").slice(0, 2);
    const sign = whole.startsWith("-") ? -1n : 1n;
    total += sign * BigInt(whole.replace("-", "") + padded);
  }
  return total;
}

function minorToString(value: bigint): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(3, "0");
  return `${negative ? "-" : ""}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

export interface JournalsPageProps {
  /** The company's base currency, or null when it has no ledger settings. */
  currency: string | null;
  canPost?: boolean;
  canReverse?: boolean;
}

export function JournalsPage({ currency, canPost = true, canReverse = true }: JournalsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const accountsQuery = useAccounts({ is_postable: true });
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const entriesQuery = useQuery({
    queryKey: ["ledger/journal-entries", "list"],
    queryFn: ({ signal }) => listJournalEntries({ limit: 50 }, signal),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [postingDate, setPostingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState<DraftLine[]>([EMPTY_LINE, EMPTY_LINE]);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingEvent, setPendingEvent] = useState<BusinessEvent | null>(null);

  const debitTotal = sumMinor(lines.map((line) => line.debit));
  const creditTotal = sumMinor(lines.map((line) => line.credit));
  const difference = debitTotal - creditTotal;
  const balanced = difference === 0n && debitTotal > 0n;

  // Following the event is the only honest way to report the outcome: the
  // submit answered "accepted", which is not "posted".
  const eventQuery = useQuery<BusinessEvent>({
    queryKey: ["ledger/business-events", pendingEvent?.id ?? null],
    enabled: pendingEvent !== null && !TERMINAL_STATES.has(pendingEvent.status),
    // Poll until the ledger reaches a terminal state, then stop.
    refetchInterval: (query) =>
      TERMINAL_STATES.has(query.state.data?.status ?? "") ? false : 1000,
    queryFn: ({ signal }) => getBusinessEvent(pendingEvent?.id ?? "", signal),
  });

  const settledEvent = eventQuery.data ?? pendingEvent;
  const settledState: string = settledEvent === null ? "" : (settledEvent.status ?? "");
  if (TERMINAL_STATES.has(settledState) && entriesQuery.isStale === false) {
    // The list is refetched once the event reaches a terminal state, so the
    // new entry appears without the operator reloading.
    void queryClient.invalidateQueries({ queryKey: ["ledger/journal-entries"] });
  }

  const accountLabel = (accountId: string): string => {
    const account = accounts.find((candidate) => candidate.id === accountId);
    return account === undefined
      ? accountId
      : `${account.code} — ${localizedName(account.name, account.name_ar, i18n.language)}`;
  };

  const submitMutation = useIdempotentMutation<
    BusinessEvent,
    { posting_date: string; lines: ManualJournalLine[] }
  >({
    mutationFn: (body, key) => submitManualJournal(body, key),
    onSuccess: (event) => {
      setPendingEvent(event);
      setDialogOpen(false);
      setLines([EMPTY_LINE, EMPTY_LINE]);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const reverseMutation = useIdempotentMutation<unknown, { entryId: string }>({
    mutationFn: ({ entryId }, key) => reverseJournalEntry(entryId, {}, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ledger/journal-entries"] });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = (): void => {
    // A company with no base currency cannot post; the control that opens this
    // form is not rendered, and this is the second lock on the same door.
    if (currency === null) return;
    const wire: ManualJournalLine[] = [];
    for (const [index, line] of lines.entries()) {
      const debit = line.debit.trim();
      const credit = line.credit.trim();
      if (line.accountId === "" && debit === "" && credit === "") continue;
      if (line.accountId === "") {
        setFormError(t("ledger.journals.lineNeedsAccount", { line: index + 1 }));
        return;
      }
      if ((debit === "") === (credit === "")) {
        setFormError(t("ledger.journals.lineNeedsOneSide", { line: index + 1 }));
        return;
      }
      // The wire carries one signed amount: positive debits, negative credits.
      const amount = debit === "" ? `-${credit}` : debit;
      wire.push({
        role: debit === "" ? "credit" : "debit",
        account_id: line.accountId,
        currency,
        amount,
      });
    }
    if (wire.length < 2) {
      setFormError(t("ledger.journals.needsTwoLines"));
      return;
    }
    if (!balanced) {
      setFormError(t("ledger.journals.mustBalance", { difference: minorToString(difference) }));
      return;
    }
    setFormError(null);
    submitMutation.mutate({ posting_date: postingDate, lines: wire });
  };

  const entries = entriesQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">
          {t("ledger.journals.title")}
        </h1>
        {canPost && currency !== null ? (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="me-2 size-4" aria-hidden="true" />
            {t("ledger.journals.new")}
          </Button>
        ) : null}
      </div>

      {canPost && currency === null ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t(
            "ledger.journals.noBaseCurrency",
            "This company has no base currency yet, so nothing can be posted. Set one in the ledger settings first.",
          )}
        </p>
      ) : null}

      {settledEvent !== null ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-4 text-sm">
            <Badge variant={settledState === "posted" ? "default" : "secondary"}>
              {t(`status.${settledState}`, settledState || "pending")}
            </Badge>
            <span>
              {settledState === "posted"
                ? t("ledger.journals.posted")
                : settledState === "failed"
                  ? t("ledger.journals.failed")
                  : settledState === "skipped"
                    ? t("ledger.journals.skipped")
                    : t("ledger.journals.awaiting")}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ms-auto"
              onClick={() => setPendingEvent(null)}
            >
              {t("actions.close")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("ledger.journals.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {entriesQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("errors.generic")}
            </p>
          ) : entriesQuery.isPending ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("table.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{t("ledger.journals.number")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.journals.date")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.journals.source")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("ledger.journals.amount")}</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b transition-colors last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs">
                        {entry.entry_no ?? entry.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {formatCalendarDate(entry.posting_date, i18n.language)}
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {entry.source_module ?? "—"}
                      </td>
                      <td className="px-4 py-3 align-top tabular-nums">
                        <MoneyDisplay value={entry.base_total} />
                      </td>
                      <td className="px-4 py-3 align-top text-end">
                        {canReverse ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={reverseMutation.isPending}
                            onClick={() => reverseMutation.mutate({ entryId: entry.id })}
                            aria-label={`${t("ledger.journals.reverse")} ${entry.entry_no ?? entry.id}`}
                          >
                            <Undo2 className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("ledger.journals.new")}</DialogTitle>
            <DialogDescription>{t("ledger.journals.formHint")}</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="journal-date">{t("ledger.journals.date")}</Label>
              <Input
                id="journal-date"
                type="date"
                dir="ltr"
                className="w-48"
                value={postingDate}
                onChange={(event) => setPostingDate(event.target.value)}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-medium text-muted-foreground">
                    <th className="px-2 py-2 font-medium">{t("ledger.journals.account")}</th>
                    <th className="px-2 py-2 font-medium">{t("ledger.journals.debit")}</th>
                    <th className="px-2 py-2 font-medium">{t("ledger.journals.credit")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="px-2 py-2">
                        <Select
                          value={line.accountId === "" ? undefined : line.accountId}
                          onValueChange={(value) =>
                            setLines((current) =>
                              current.map((item, position) =>
                                position === index ? { ...item, accountId: value } : item,
                              ),
                            )
                          }
                        >
                          <SelectTrigger
                            aria-label={`${t("ledger.journals.account")} ${index + 1}`}
                          >
                            <SelectValue placeholder={t("ledger.journals.chooseAccount")} />
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {accountLabel(account.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={`${t("ledger.journals.debit")} ${index + 1}`}
                          value={line.debit}
                          onChange={(event) =>
                            setLines((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...item, debit: event.target.value, credit: "" }
                                  : item,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={`${t("ledger.journals.credit")} ${index + 1}`}
                          value={line.credit}
                          onChange={(event) =>
                            setLines((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...item, credit: event.target.value, debit: "" }
                                  : item,
                              ),
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLines((current) => [...current, EMPTY_LINE])}
              >
                {t("ledger.journals.addLine")}
              </Button>
              <span className="tabular-nums">
                {t("ledger.journals.debitTotal")} {minorToString(debitTotal)}
              </span>
              <span className="tabular-nums">
                {t("ledger.journals.creditTotal")} {minorToString(creditTotal)}
              </span>
              <span
                className={difference === 0n ? "text-muted-foreground" : "text-destructive"}
                data-testid="journal-difference"
              >
                {t("ledger.journals.difference")} {minorToString(difference)}
              </span>
            </div>

            {formError !== null ? (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={!balanced || submitMutation.isPending}>
                {t("ledger.journals.post")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
